import { getSchedulingWindow } from '../endpoints/getSchedulingWindow';
import { evaluateSchedulingWindow, type OpenPeriod } from '../endpoints/evaluateSchedulingWindow';

export interface OpenPeriodsPreview {
  /** The window as saved, for the heading — the caller may only know its id. */
  readonly windowName: string;
  readonly zoneName: string;
  /** The zone the evaluation ran in, and therefore the one the periods are read in. */
  readonly timezone: string;
  readonly periods: readonly OpenPeriod[];
}

/**
 * When a window's openings can be previewed from, and the periods that follow.
 *
 * Orchestrates two endpoints because the evaluate route needs a timezone and the
 * caller only has a row: the window is read first for its saved zone, then
 * evaluated in it. Sending no timezone is not an option — the backend then falls
 * back to a hardcoded `Europe/Amsterdam`.
 *
 * **Evaluates the window as saved.** Nothing here can preview unsaved edits: the
 * route resolves the window by zone and name and reads the database. That is why
 * the preview is reached from the list rather than from inside the edit dialog,
 * where it would look like feedback on the rules currently on screen.
 *
 * Deliberately impure — it evaluates from "now", so the same arguments give a
 * different answer as time passes. Callers that cache it must re-ask rather than
 * compare inputs.
 */
export const previewOpenPeriods = async (id: number): Promise<OpenPeriodsPreview> => {
  const window = await getSchedulingWindow(id);
  if (window === null) {
    throw new Error('Scheduling window not found; the list may be out of date. Please refresh.');
  }
  if (window.zone_name === null) {
    throw new Error(`Scheduling window '${window.name}' has no zone, so it cannot be evaluated.`);
  }

  const { schedule_open_in_given_period } = await evaluateSchedulingWindow({
    zoneName: window.zone_name,
    windowName: window.name,
    timestamp: new Date().toISOString(),
    timezone: window.timezone,
  });

  return {
    windowName: window.name,
    zoneName: window.zone_name,
    timezone: window.timezone,
    periods: schedule_open_in_given_period,
  };
};
